// Components==============
import showAudioExercise from 'actions/audioExercise/show';
import updateExerciseStatus from 'actions/authentication/updateExerciseStatus';
import BackButton from 'components/Layout/BackButton/BackButton';
import Background from 'components/Layout/Background/Background';
import Menu from 'components/Layout/Menu/Menu';
import NavigationBox, {
    Displaymode,
} from 'components/Layout/NavigationBox/NavigationBox';
import ProgressBar from 'components/UI/ProgressBar/ProgressBar';
import React, { createContext, useEffect, useState } from 'react';
import Confetti from 'react-dom-confetti';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import './AudioExercise.module.scss';
import ConditionalExercise from './components/ConditionalExercise';
// =========================

type AudioExerciseContextTypes = {
    exercise: AudioExerciseEntity;
    isFinished: boolean;
    setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
    setProgress: React.Dispatch<React.SetStateAction<ProgressType>>;
    background: Background;
};

export const AudioExerciseContext = createContext(
    {} as AudioExerciseContextTypes
);

export default function AudioExercise() {
    const dispatch = useDispatch();
    const { exerciseId, treatmentId } = useParams() as {
        exerciseId: string;
        treatmentId: string;
    };

    useEffect(() => {
        dispatch(showAudioExercise(exerciseId));
    }, []);

    const [isFinished, setIsFinished] = useState(false);
    const [top, setTop] = useState('2em');
    const [displayMode, setDisplayMode] = useState<Displaymode>('centered-y');

    const exercise = useSelector(
        ({ audioExercise }: { audioExercise: AudioExercise }) =>
            audioExercise.entities.byId[exerciseId]
    );

    const explanations: any = {
        clap_sound: {
            text:
                'Klik op de groene bel als je de klank hoort die hierboven staat. Hoor je een andere klank, klik dan op de rode bel.',
            audio: '/audio/exercise_explanation/clap_sound_new.mp3',
        },
        clap_word: {
            text:
                'Klik op de groene bel als je de klank die hierboven staat in het woord hoort. Hoor je een andere klank in het woord, klik dan op de rode bel.',
            audio: '/audio/exercise_explanation/clap_word_new.mp3',
        },
        find_wrong: {
            text:
                'Klik op afspelen om de 4 klanken te horen. Luister goed en klik op de pijl onder de klank die er niet bij hoort.',
            audio: '/audio/exercise_explanation/find_wrong.mp3',
        },
        '4-7': {
            create_word: {
                text:
                    'Je hoort een woord. Zeg het woord na en luister goed wat je met het woord moet doen.',
                audio: '/audio/exercise_explanation/create_word.mp3',
            },
            name_word: {
                text:
                    'Je hoort een woord. Zeg het woord na en benoem het woord dat overblijft.',
                audio: '/audio/exercise_explanation/name_word.mp3',
            },
            name_sound: {
                text:
                    'Je hoort een woord. Zeg het woord na en benoem de juiste klank uit het woord.',
                audio: '/audio/exercise_explanation/name_sound.mp3',
            },
            name_sound_in_word: {
                text:
                    'Je hoort een woord. Zeg het woord na en benoem het woord in klanken.',
                audio: '/audio/exercise_explanation/name_sound_in_word.mp3',
            },
        },
    };

    useEffect(() => {
        if (isFinished) {
            setTop('50%');
            setDisplayMode('centered');
        }
    }, [isFinished]);

    const background = useSelector(
        (state: ReduxStore) =>
            state?.treatment?.entities?.byId[treatmentId]?.world || 'city'
    );

    const [progress, setProgress] = useState<ProgressType>({
        total: 0,
        done: 0,
    });

    const user = useSelector(
        (state: ReduxStore) => state?.authentication?.user
    );

    const isAlreadyFinished =
        typeof user?.exercise_status?.find(
            (e) =>
                e.exercise_id === exercise?.id &&
                e.exercise_type.includes('AudioExercise')
        ) !== 'undefined';

    useEffect(() => {
        if (isFinished && !isAlreadyFinished)
            dispatch(
                updateExerciseStatus({
                    exercise_id: exercise?.id,
                    exercise_type: 'audio',
                })
            );
    }, [isFinished]);

    const layout = exercise?.exercise?.[0]?.layout;

    return (
        <AudioExerciseContext.Provider
            value={{
                exercise,
                isFinished,
                setIsFinished,
                setProgress,
                background,
            }}
        >
            <Background background={background}>
                <BackButton />
                <div
                    style={{
                        zIndex: 10,
                    }}
                >
                    <Confetti active={isFinished} />
                </div>
                <NavigationBox
                    displayMode={displayMode}
                    subtitle={
                        isFinished
                            ? 'Je hebt de oefening gemaakt. Goed gedaan!'
                            : exercise?.name
                    }
                    text={
                        isFinished
                            ? undefined
                            : exercise?.type === '4-7'
                            ? layout &&
                              explanations[exercise?.type]?.[layout].text
                            : explanations[exercise?.type]?.text
                    }
                    audio={
                        isFinished
                            ? undefined
                            : exercise?.type === '4-7'
                            ? layout &&
                              explanations[exercise?.type]?.[layout].audio
                            : explanations[exercise?.type]?.audio
                    }
                    top={top}
                    finishedButtons={isFinished && 'audio-exercise'}
                    ProgressBar={
                        !isFinished && (
                            <ProgressBar progressProps={[progress]} />
                        )
                    }
                />
                <Menu />
                <ConditionalExercise />
            </Background>
        </AudioExerciseContext.Provider>
    );
}
